import { graphql, Link } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import React, { useState } from "react";
import { productUrl } from "../../../node-product";
import Europea from "../../assets/icons/europea.svg";
import { productAlt } from "../../utils/product-alt";

// Champs nécessaires aux badges de la carte produit (schéma aligné FR) : à inclure dans
// chaque requête qui alimente ProductList / ProductCard (`...ProductCardFields`).
export const productCardFields = graphql`
  fragment ProductCardFields on StrapiProduct {
    attributs_grammages {
      Nom
    }
    attributs_labels {
      Label
    }
    made_in_france
    best_seller
  }
`;

export const ProductCard = ({ product, cols, className, color }) => {
  const [hoveredImage, setHoveredImage] = useState(null);

  const displayImage = hoveredImage || (product.image ? getImage(product.image.localFile) : null);
  const wrapperClass = className ?? `w-6/12 ${cols === 3 ? "md:w-4/12" : "md:w-3/12"} p-1`;

  return (
    <div className={wrapperClass}>
      <Link to={productUrl(product)} className="h-full p-1 flex flex-col justify-between border-gray-200 border-1">
        <div className="relative mb-0 overflow-hidden">
          {/* Badges izquierda : gramaje + etiqueta */}
          <div className="absolute top-2 left-0 z-10 flex flex-col gap-1 my-2">
            {product.attributs_grammages && (
              <span className="bg-blue-hover text-white text-sm px-2 py-1 rounded-r">
                {product.attributs_grammages.Nom} gr
              </span>
            )}
            {product.attributs_labels && product.attributs_labels.Label === "GOTS" && (
              <span className="block bg-green-badge text-white text-sm px-2 py-1 rounded-r">
                BIO
              </span>
            )}
          </div>

          {/* Badges derecha : cinta best seller + fabricación europea */}
          {product.best_seller && (
            <div className="absolute top-0 right-0 w-24 h-24 z-10 pointer-events-none">
              <span className="absolute block w-36 text-center text-white text-xs uppercase py-1 bg-blue-hover shadow-sm" style={{ transform: "rotate(33deg)", right: "-30px", top: "18px" }}>
                Best Seller
              </span>
            </div>
          )}
          {product.made_in_france && (
            <div className={`absolute ${product.best_seller ? "top-10" : "top-2"} right-2 z-10`} title="Fabricación europea">
              <Europea className="w-6 h-6" />
            </div>
          )}

          {product.variations && product.variations.length > 0 && (
            <div className="absolute top-16 lg:top-20 left-2 z-10 flex flex-col gap-1">
              {getColorSwatches(product.variations, setHoveredImage)}
            </div>
          )}
          <div>
            {displayImage && (
              <GatsbyImage
                className="w-full h-full"
                alt={productAlt(product.title, color)}
                image={displayImage}
              />
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-around mt-0">
          <div className="text-center rounded bg-blue text-white uppercase py-1 px-2 text-sm flex flex-col items-center">
            {formatProductTitle(product.title)}
          </div>
        </div>
      </Link>
    </div>
  );
};

const ProductList = ({ products, cols = 3, color }) => {
  return (
    <div className="flex flex-wrap ">
      {products.map((product, index) => (
        <ProductCard key={index} product={product} cols={cols} color={color} />
      ))}
    </div>
  );
};

function formatProductTitle(title) {
  const matches = title.match(/^([^(]+)(\(.+\))$/);
  if (matches) {
    return (
      <>
        <span>{matches[1]}</span>
        <span>{matches[2]}</span>
      </>
    );
  }
  return <span>{title}</span>;
}

function getColorSwatches(variations, setHoveredImage) {
  const uniqueVariations = variations
    .filter((v) => v.attributs_couleur)
    .filter((v, index, self) =>
      index === self.findIndex((c) => c.attributs_couleur.Nom === v.attributs_couleur.Nom)
    ); // Supprimer les doublons par nom

  const maxDisplay = 7;
  const displayVariations = uniqueVariations.slice(0, maxDisplay);
  const remainingCount = uniqueVariations.length - maxDisplay;

  return (
    <>
      {displayVariations.map((variation, index) => (
        <span
          key={variation.attributs_couleur.id || index}
          title={variation.attributs_couleur.Nom}
          className="block w-4 h-4 rounded-full shadow border border-gray-300 cursor-pointer"
          style={{
            backgroundColor: variation.attributs_couleur.valeur || "#ccc",
          }}
          onMouseEnter={() => {
            if (variation.Image?.localFile) {
              setHoveredImage(getImage(variation.Image.localFile));
            }
          }}
          onMouseLeave={() => setHoveredImage(null)}
        />
      ))}
      {remainingCount > 0 && (
        <span className="w-4 h-4 rounded-full bg-gray-200 border border-gray-300 text-[8px] flex items-center justify-center text-gray-700 font-semibold">
          +{remainingCount}
        </span>
      )}
    </>
  );
}

export default ProductList;
